import { useRef } from 'react';
import PropTypes from 'prop-types';
import startCase from 'lodash/startCase';
import { Form, Field } from 'react-final-form';
import setFieldTouched from 'final-form-set-field-touched';

import { getPlainValues } from 'shared/functions-ts';
import { returnFinalFormError, validateRequiredFn } from 'shared/functions';
import { Input } from 'shared/ui/Input';
import SpinnerSmall from 'shared/ui/Spinner/SpinnerSmall';

import { Immutable } from 'types/common-types';
import './FormEntity.scss';

interface FormEntityProps<E> {
  initialValues: E | Immutable<E>;
  entityIdKey: string;
  entityLabelKey: string;
  entityTypeKey: string;
  onSubmit: (entity: E) => Promise<unknown>;
  onCancel?: (entityId: number, mode: boolean) => void;
}

const mutators = {
  setFieldTouched,
};

const resetForm = (form) => {
  form.reset();
  form.getRegisteredFields().forEach((name) => {
    form.resetFieldState(name);
    form.mutators.setFieldTouched(name, false);
  });
};

const FormEntity = <E,>(props: FormEntityProps<E>) => {
  const { entityIdKey, entityTypeKey, entityLabelKey, initialValues = {}, onSubmit, onCancel } = props;

  const formRef = useRef(null);

  const plainValues = getPlainValues(initialValues);

  const showCancel = typeof onCancel === 'function';

  const entityId = plainValues?.[entityIdKey] as number;

  const nameLabel = `Enter ${startCase(entityTypeKey)} name`;

  const submit = (values: E) =>
    onSubmit(values)
      .then(() => {
        if (formRef?.current) {
          resetForm(formRef.current);
        }
      })
      .catch(returnFinalFormError(values));

  return (
    <Form
      mutators={mutators}
      initialValues={initialValues}
      onSubmit={submit}
      render={(renderProps) => {
        const { error, submitError, submitting, form, handleSubmit } = renderProps;

        formRef.current = form;

        const errorMessage = error || submitError;

        return (
          <form className="editor-inline-form" onSubmit={handleSubmit}>
            <div key="name" className="editor-inline-form-field">
              <Field
                className="form-control"
                component={Input}
                name={entityLabelKey}
                labelClass="hide"
                label={nameLabel}
                validate={validateRequiredFn}
              />
            </div>
            <div key="buttons" className="editor-inline-form-buttons">
              {errorMessage && <div className="text-danger padding">{errorMessage}</div>}

              <button
                id="submit-entity"
                type="submit"
                className="btn btn-sm btn-success margin-left"
                disabled={submitting}
              >
                {submitting ? <SpinnerSmall /> : <i className="lnr lnr-checkmark-circle" />}
              </button>
              {showCancel && (
                <button
                  id="cancel-entity"
                  type="button"
                  className="btn btn-sm btn-default margin-left"
                  disabled={submitting}
                  onClick={() => onCancel(entityId, false)}
                >
                  {submitting ? <SpinnerSmall /> : <i className="lnr lnr-cross-circle" />}
                </button>
              )}
            </div>
          </form>
        );
      }}
    />
  );
};

FormEntity.defaultProps = {
  entityLabelKey: 'name',
};

FormEntity.propTypes = {
  entityIdKey: PropTypes.string.isRequired,
  entityLabelKey: PropTypes.string.isRequired,
  entityTypeKey: PropTypes.string.isRequired,
  initialValues: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

// eslint-disable-next-line import/no-default-export
export default FormEntity;
